import classNames from "classnames";
import { forwardRef, HTMLAttributes } from "react";
import { Flex } from "../../../flex";
import { FlattenedItem, TreeItemType } from "../types";
import { RedoFolderItem } from "./redo-folder-item";
import { RedoViewItem } from "./redo-view-item";
import * as styles from "./tree-item.module.css";

export interface TreeItemProps extends Omit<
  HTMLAttributes<HTMLLIElement>,
  "id"
> {
  value: FlattenedItem;
  depth: number;
  indentationWidth: number;
  clone?: boolean;
  ghost?: boolean;
  handleProps?: any;
  onToggleExpanded?(): void;
  onRequestEdit?: () => void;
  wrapperRef?(node: HTMLLIElement): void;
  childrenLinks?: string[];
  /** Total direct children; sizes the folder's connector pipe when expanded. */
  childCount?: number;
  expanded?: boolean;
  /** State-driven selection flag. Undefined falls back to URL matching. */
  selected?: boolean;
  /** When provided, body-click fires this instead of URL navigation or toggle. */
  onSelect?: () => void;
  /**
   * Fires on every item click (folder or view) in addition to the primary action.
   * Use for analytics that should track engagement without replacing navigation/toggle.
   */
  onItemClick?: (item: FlattenedItem) => void;
  /** When true, drag is disabled and the drag handle is not rendered. */
  disableDrag?: boolean;
}

export const TreeItemComponent = forwardRef<HTMLDivElement, TreeItemProps>(
  function TreeItemComponent(
    {
      clone,
      depth,
      ghost,
      handleProps,
      indentationWidth,
      onToggleExpanded,
      style,
      value,
      wrapperRef,
      onRequestEdit,
      childrenLinks,
      childCount,
      expanded,
      selected,
      onSelect,
      onItemClick,
      disableDrag,
    },
    ref,
  ) {
    const isFolder = value.type === TreeItemType.Folder;
    // Folder actions are the primary way to add things, so they stay visible.
    // Row overflow menus reveal on hover to keep the list quiet.
    const rowAction = isFolder ? value.trailingAction : value.overflowMenu;

    return (
      <Flex
        className={classNames(
          styles.wrapper,
          ghost && styles.ghost,
          clone && styles.clone,
        )}
        gap="none"
        ref={wrapperRef}
        style={
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          {
            "--spacing": `${indentationWidth * depth}px`,
          } as React.CSSProperties
        }
      >
        <Flex
          align="center"
          gap="none"
          position="relative"
          ref={ref}
          style={style}
          w="full"
        >
          {value.type === TreeItemType.Folder ? (
            <RedoFolderItem
              childCount={childCount}
              childrenLinks={childrenLinks ?? []}
              clone={clone}
              data-dd-action-name={value["data-dd-action-name"]}
              disableDrag={disableDrag}
              dragHandleProps={handleProps}
              expanded={!!expanded}
              friendlyName={value.friendlyName}
              hasChildren={value.children.length > 0}
              Icon={value.Icon}
              IconCollapsed={value.IconCollapsed}
              name={value.name}
              notificationCount={value.notificationCount}
              onClick={onSelect}
              onItemClick={onItemClick ? () => onItemClick(value) : undefined}
              onRequestEdit={onRequestEdit}
              onToggleExpanded={onToggleExpanded ?? (() => {})}
              selected={selected}
            />
          ) : (
            <RedoViewItem
              clone={clone}
              data-dd-action-name={value["data-dd-action-name"]}
              dimmed={value.dimmed}
              disableDrag={disableDrag}
              dragHandleProps={handleProps}
              friendlyName={value.friendlyName}
              Icon={value.Icon}
              linkHref={value.actionHref}
              name={value.name}
              notificationCount={value.notificationCount}
              onClick={onSelect}
              onItemClick={onItemClick ? () => onItemClick(value) : undefined}
              selected={selected}
            />
          )}
        </Flex>
        {rowAction ? (
          /* Overlay on the wrapper, never inside the row: the row is a
             <button>, so a nested button would be invalid markup. */
          <div
            className={classNames(
              styles.rowAction,
              isFolder && styles.rowActionFolder,
              !isFolder && styles.rowActionHoverOnly,
              selected && styles.show,
            )}
          >
            {rowAction}
          </div>
        ) : null}
      </Flex>
    );
  },
);
